import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    ArcElement,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Filler,
    Legend,
    LineElement,
    LinearScale,
    PointElement,
    Tooltip,
} from 'chart.js'
import { Bar, Doughnut, Line } from 'react-chartjs-2'
import styles from './styles.module.scss'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../helpers/apiRequester'
import {
    FaUpload,
    FaFileExcel,
    FaChevronDown,
    FaFileCsv,
    FaTrashAlt,
    FaChartBar,
    FaChartLine,
    FaChartPie,
    FaLayerGroup,
    FaUsers,
    FaFileAlt,
    FaPoundSign,
    FaCogs,
} from 'react-icons/fa'
import { useAuthUserData } from '../../../authUserProvider'
import { brandPalette, brandPaletteAlpha } from '../homeView/chartHelpers'
import { BatchReportBuilder } from './batchReportBuilder'

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Tooltip,
    Legend,
    Filler
)

type StatusFilter = 'all' | 'requested' | 'not_requested'
type SortField = 'status' | 'value' | 'clients' | 'batch_id' | 'created' | 'nera_id'

interface FundingBatch {
    batch_id: string
    allocation_date: string
    created_at?: string
    total_clients: number
    total_claims: number
    book_value: number
    draw_down_value: number
    introducer: string
    allocated: number
    submitted: number
    api_transferred: number
    config?: {
        commission_rate?: number
        stat_interest?: number
        max_clients?: number
        min_client_value?: number
        calculation_date?: string
        ate_requested?: boolean
        ate_requested_date?: string
        ate_returned?: boolean
        nera_id?: string
        batch_creation_date?: string
    }
    [key: string]: any
}

interface FundingBatchTotals {
    total_batches: number
    total_clients: number
    total_claims: number
    total_book_value: number
    total_draw_down_value: number
    batches_submitted: number
    batches_ate_requested: number
    batches_ate_returned: number
    batches_api_transferred: number
    audit_removed_clients: number
    audit_removed_claims: number
}

interface ImportPreview {
    batchGroup: string
    totalRows: number
    uniqueClients: number
    totalClaims: number
    introducers: string[]
    facilities: string[]
}

interface ImportResult {
    batchGroup: string
    summary: {
        totalRows: number
        uniqueClients: number
        totalClaims: number
        introducers: string[]
        facilities: string[]
    }
    insertResults: {
        totalInserted: number
        totalFailed: number
        clients: { successful: number; failed: number }
        claims: { successful: number; failed: number }
        references: { successful: number; failed: number }
    }
}

interface RemoveByEmailPreview {
    requestedEmails: number
    matchedClients: number
    notFoundCount: number
    notFoundSample?: string[]
}

interface RemoveByEmailResult {
    requestedEmails: number
    matchedClients: number
    notFoundCount: number
    notFoundSample?: string[]
    deletionResults: {
        funder_claims: number
        funder_references: number
        funder_clients: number
    }
}

export const AllocateClaimsView = () => {
    const navigate = useNavigate()
    const authUser = useAuthUserData()
    const isScoreUser = authUser?.['custom:organisation'] === 'Score'
    const [fundingBatches, setFundingBatches] = useState<FundingBatch[]>([])
    const [fundingBatchTotals, setFundingBatchTotals] = useState<FundingBatchTotals | null>(null)
    const [fundingBatchesLoading, setFundingBatchesLoading] = useState(false)
    const [batchIdSearch, setBatchIdSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
    const [sortBy, setSortBy] = useState<SortField>('nera_id')
    const [sortDesc, setSortDesc] = useState(true)

    const [batchControlsOpen, setBatchControlsOpen] = useState(false)
    const [importFile, setImportFile] = useState<File | null>(null)
    const [importFileBase64, setImportFileBase64] = useState<string | null>(null)
    const [importPreview, setImportPreview] = useState<ImportPreview | null>(null)
    const [importPreviewLoading, setImportPreviewLoading] = useState(false)
    const [importLoading, setImportLoading] = useState(false)
    const [importResult, setImportResult] = useState<ImportResult | null>(null)
    const [importError, setImportError] = useState<string | null>(null)
    const [importBatchGroup, setImportBatchGroup] = useState('')
    const [importCommissionRate, setImportCommissionRate] = useState('3')
    const [importStatInterest, setImportStatInterest] = useState('8')
    const [importMinClientValue, setImportMinClientValue] = useState('0')
    const [importCalcDate, setImportCalcDate] = useState(new Date().toISOString().split('T')[0])
    const importFileRef = useRef<HTMLInputElement>(null)

    const [removeFile, setRemoveFile] = useState<File | null>(null)
    const [removeFileBase64, setRemoveFileBase64] = useState<string | null>(null)
    const [removePreview, setRemovePreview] = useState<RemoveByEmailPreview | null>(null)
    const [removePreviewLoading, setRemovePreviewLoading] = useState(false)
    const [removeLoading, setRemoveLoading] = useState(false)
    const [removeError, setRemoveError] = useState<string | null>(null)
    const [removeResult, setRemoveResult] = useState<RemoveByEmailResult | null>(null)
    const [removeConfirmed, setRemoveConfirmed] = useState(false)
    const removeFileRef = useRef<HTMLInputElement>(null)

    const handleImportFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        setImportFile(file)
        setImportResult(null)
        setImportError(null)
        setImportPreview(null)

        const reader = new FileReader()
        reader.onload = async (event) => {
            const base64 = (event.target?.result as string).split(',')[1]
            setImportFileBase64(base64)

            try {
                setImportPreviewLoading(true)
                const response = await api({ url: '/funding-batch-import', data: { fileData: base64, preview: true } })
                if (response.data?.success) {
                    const p = response.data as ImportPreview & { success: boolean; preview: boolean }
                    setImportPreview({
                        batchGroup: p.batchGroup,
                        totalRows: p.totalRows,
                        uniqueClients: p.uniqueClients,
                        totalClaims: p.totalClaims,
                        introducers: p.introducers,
                        facilities: p.facilities
                    })
                    setImportBatchGroup(p.batchGroup || '')
                } else {
                    setImportError(response.data?.message || 'Failed to preview file')
                }
            } catch (err: any) {
                setImportError(err?.response?.data?.message || err?.message || 'Failed to preview file')
            } finally {
                setImportPreviewLoading(false)
            }
        }
        reader.readAsDataURL(file)
    }, [])

    const handleImportSubmit = useCallback(async () => {
        if (!importFileBase64 || !importBatchGroup) return
        try {
            setImportLoading(true)
            setImportError(null)
            setImportResult(null)
            const response = await api({
                url: '/funding-batch-import',
                data: {
                    fileData: importFileBase64,
                    preview: false,
                    batchGroup: importBatchGroup,
                    commissionRate: importCommissionRate,
                    statInterest: importStatInterest,
                    minClientValue: importMinClientValue,
                    calculationDate: importCalcDate
                }
            })
            if (response.data?.success) {
                setImportResult(response.data as ImportResult)
                setImportFile(null)
                setImportFileBase64(null)
                setImportPreview(null)
                if (importFileRef.current) importFileRef.current.value = ''

                const fetchFundingBatches = async () => {
                    try {
                        setFundingBatchesLoading(true)
                        const batchResponse = await api({ url: '/funding-batches', data: {} })
                        if (batchResponse.data?.success) {
                            const batches = batchResponse.data.batches || []
                            batches.sort((a: FundingBatch, b: FundingBatch) => {
                                const dateA = new Date(a.created_at || a.allocation_date || 0).getTime()
                                const dateB = new Date(b.created_at || b.allocation_date || 0).getTime()
                                return dateB - dateA
                            })
                            setFundingBatches(batches)
                            if (batchResponse.data.totals) {
                                setFundingBatchTotals(batchResponse.data.totals)
                            }
                        }
                    } catch {
                        // batch list refresh failed silently
                    } finally {
                        setFundingBatchesLoading(false)
                    }
                }
                fetchFundingBatches()
            } else {
                setImportError(response.data?.message || 'Import failed')
            }
        } catch (err: any) {
            if (err?.response?.status === 401 || err?.status === 401) {
                Report.failure('Session Expired', 'Your session has expired. Please log in again.', 'OK')
                navigate('/login')
                return
            }
            setImportError(err?.response?.data?.message || err?.message || 'Import failed')
        } finally {
            setImportLoading(false)
        }
    }, [importFileBase64, importBatchGroup, importCommissionRate, importStatInterest, importMinClientValue, importCalcDate, navigate])

    const handleRemoveFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        setRemoveFile(file)
        setRemoveResult(null)
        setRemoveError(null)
        setRemovePreview(null)
        setRemoveConfirmed(false)

        const reader = new FileReader()
        reader.onload = async (event) => {
            const base64 = (event.target?.result as string).split(',')[1]
            setRemoveFileBase64(base64)

            try {
                setRemovePreviewLoading(true)
                const response = await api({
                    url: '/funding-clients-remove-by-email-csv',
                    data: { fileData: base64, preview: true },
                })
                if (response.data?.success) {
                    const p = response.data as RemoveByEmailPreview & { success: boolean; preview?: boolean }
                    setRemovePreview({
                        requestedEmails: p.requestedEmails,
                        matchedClients: p.matchedClients,
                        notFoundCount: p.notFoundCount,
                        notFoundSample: p.notFoundSample,
                    })
                } else {
                    setRemoveError(response.data?.message || 'Failed to analyse CSV')
                }
            } catch (err: any) {
                setRemoveError(err?.response?.data?.message || err?.message || 'Failed to analyse CSV')
            } finally {
                setRemovePreviewLoading(false)
            }
        }
        reader.readAsDataURL(file)
    }, [])

    const handleRemoveSubmit = useCallback(async () => {
        if (!removeFileBase64 || !removeConfirmed) return
        try {
            setRemoveLoading(true)
            setRemoveError(null)
            setRemoveResult(null)
            const response = await api({
                url: '/funding-clients-remove-by-email-csv',
                data: { fileData: removeFileBase64, preview: false },
            })
            if (response.data?.success) {
                setRemoveResult(response.data as RemoveByEmailResult)
                setRemoveFile(null)
                setRemoveFileBase64(null)
                setRemovePreview(null)
                setRemoveConfirmed(false)
                if (removeFileRef.current) removeFileRef.current.value = ''
            } else {
                setRemoveError(response.data?.message || 'Removal failed')
            }
        } catch (err: any) {
            if (err?.response?.status === 401 || err?.status === 401) {
                Report.failure('Session Expired', 'Your session has expired. Please log in again.', 'OK')
                navigate('/login')
                return
            }
            setRemoveError(err?.response?.data?.message || err?.message || 'Removal failed')
        } finally {
            setRemoveLoading(false)
        }
    }, [removeFileBase64, removeConfirmed, navigate])

    // Fetch funding batches on component mount
    useEffect(() => {
        const fetchFundingBatches = async () => {
            try {
                setFundingBatchesLoading(true)
                const response = await api({ url: '/funding-batches', data: {} })

                if (response.data && response.data.success) {
                    const batches = response.data.batches || []
                    // Sort batches by created_at (newest first), fallback to allocation_date if created_at not available
                    const sortedBatches = batches.sort((a: FundingBatch, b: FundingBatch) => {
                        // Try to use created_at first, fallback to allocation_date
                        const dateA = new Date(a.created_at || a.allocation_date || 0).getTime()
                        const dateB = new Date(b.created_at || b.allocation_date || 0).getTime()
                        return dateB - dateA // Newest first
                    })
                    setFundingBatches(sortedBatches)
                    if (response.data.totals) {
                        setFundingBatchTotals(response.data.totals)
                    } else {
                        setFundingBatchTotals(null)
                    }
                } else {
                    setFundingBatches([])
                    setFundingBatchTotals(null)
                }
            } catch (error: any) {
                if (error?.response?.status === 401 || error?.status === 401) {
                    Report.failure('Session Expired', 'Your session has expired. Please log in again.', 'OK')
                    navigate('/login')
                    return
                }
                console.error('Error fetching funding batches:', error)
                setFundingBatches([])
                setFundingBatchTotals(null)
            } finally {
                setFundingBatchesLoading(false)
            }
        }

        fetchFundingBatches()
    }, [])

    const handleViewDetails = (batchId: string) => {
        navigate(`/funding-batch/${batchId}`)
    }

    // Prefer API totals when present; fall back to client-side sums from the full batch list
    const stats = useMemo(
        () => ({
            totalBatches: fundingBatchTotals?.total_batches ?? fundingBatches.length,
            totalClients:
                fundingBatchTotals?.total_clients ??
                fundingBatches.reduce((sum, b) => sum + (b.total_clients || 0), 0),
            totalClaims:
                fundingBatchTotals?.total_claims ??
                fundingBatches.reduce((sum, b) => sum + (b.total_claims || 0), 0),
            totalBookValue:
                fundingBatchTotals?.total_book_value ??
                fundingBatches.reduce((sum, b) => sum + (b.book_value || 0), 0),
            auditRemovedClients: fundingBatchTotals?.audit_removed_clients ?? 0,
            auditRemovedClaims: fundingBatchTotals?.audit_removed_claims ?? 0,
            totalSubmitted:
                fundingBatchTotals?.batches_submitted ??
                fundingBatches.filter((b) => b.submitted > 0).length,
            totalAteRequested:
                fundingBatchTotals?.batches_ate_requested ??
                fundingBatches.filter((b) => b.config?.ate_requested).length,
            totalAteReturned:
                fundingBatchTotals?.batches_ate_returned ??
                fundingBatches.filter((b) => b.config?.ate_returned).length,
            totalApiTransferred:
                fundingBatchTotals?.batches_api_transferred ??
                fundingBatches.filter((b) => b.api_transferred > 0).length,
            totalDrawDownValue:
                fundingBatchTotals?.total_draw_down_value ??
                fundingBatches.reduce((sum, b) => sum + (b.draw_down_value || 0), 0),
        }),
        [fundingBatches, fundingBatchTotals]
    )

    const formatStatCurrency = (value: number) =>
        `£${Number(value || 0).toLocaleString('en-GB', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`

    const formatCompactCurrency = (value: number) => {
        const num = Number(value || 0)
        if (Math.abs(num) >= 1_000_000) return `£${(num / 1_000_000).toFixed(2)}m`
        if (Math.abs(num) >= 1_000) return `£${(num / 1_000).toFixed(1)}k`
        return formatStatCurrency(num)
    }

    const formatCount = (value: number) => Number(value || 0).toLocaleString('en-GB')

    const derived = useMemo(() => {
        const allocatedClients = stats.totalClients + stats.auditRemovedClients
        const allocatedClaims = stats.totalClaims + stats.auditRemovedClaims
        const pct = (value: number, total: number) => (total > 0 ? (value / total) * 100 : 0)
        return {
            avgClientsPerBatch: stats.totalBatches > 0 ? stats.totalClients / stats.totalBatches : 0,
            avgClaimsPerClient: stats.totalClients > 0 ? stats.totalClaims / stats.totalClients : 0,
            avgValuePerClient: stats.totalClients > 0 ? stats.totalBookValue / stats.totalClients : 0,
            removedClientPct: pct(stats.auditRemovedClients, allocatedClients),
            removedClaimPct: pct(stats.auditRemovedClaims, allocatedClaims),
            submittedPct: pct(stats.totalSubmitted, stats.totalBatches),
            ateRequestedPct: pct(stats.totalAteRequested, stats.totalBatches),
            ateReturnedPct: pct(stats.totalAteReturned, stats.totalBatches),
            apiTransferredPct: pct(stats.totalApiTransferred, stats.totalBatches),
        }
    }, [stats])

    const batchDateKey = (batch: FundingBatch) =>
        batch.config?.batch_creation_date || batch.created_at || batch.allocation_date || ''

    const introducerChartData = useMemo(() => {
        const byIntroducer = new Map<string, { clients: number; claims: number }>()
        for (const batch of fundingBatches) {
            const key = (batch.introducer || 'Unknown').trim() || 'Unknown'
            const entry = byIntroducer.get(key) || { clients: 0, claims: 0 }
            entry.clients += batch.total_clients || 0
            entry.claims += batch.total_claims || 0
            byIntroducer.set(key, entry)
        }
        const rows = Array.from(byIntroducer.entries())
            .map(([introducer, values]) => ({ introducer, ...values }))
            .sort((a, b) => b.clients - a.clients || b.claims - a.claims)
        return {
            labels: rows.map((r) => r.introducer),
            datasets: [
                {
                    label: 'Clients',
                    data: rows.map((r) => r.clients),
                    backgroundColor: brandPaletteAlpha(0.75)[0],
                    borderColor: brandPalette[0],
                    borderWidth: 1,
                    borderRadius: 4,
                },
                {
                    label: 'Claims',
                    data: rows.map((r) => r.claims),
                    backgroundColor: brandPaletteAlpha(0.75)[3],
                    borderColor: brandPalette[3],
                    borderWidth: 1,
                    borderRadius: 4,
                },
            ],
        }
    }, [fundingBatches])

    const batchesOverTimeData = useMemo(() => {
        const monthMap = new Map<string, { created: number; submitted: number }>()
        for (const batch of fundingBatches) {
            const raw = batchDateKey(batch)
            if (!raw) continue
            const d = new Date(raw)
            if (Number.isNaN(d.getTime())) continue
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
            const entry = monthMap.get(key) || { created: 0, submitted: 0 }
            entry.created += 1
            if ((batch.submitted || 0) > 0) entry.submitted += 1
            monthMap.set(key, entry)
        }
        const labels = Array.from(monthMap.keys()).sort()
        let cumCreated = 0
        let cumSubmitted = 0
        const createdSeries: number[] = []
        const submittedSeries: number[] = []
        for (const label of labels) {
            const entry = monthMap.get(label)!
            cumCreated += entry.created
            cumSubmitted += entry.submitted
            createdSeries.push(cumCreated)
            submittedSeries.push(cumSubmitted)
        }
        const displayLabels = labels.map((key) => {
            const [y, m] = key.split('-')
            const date = new Date(Number(y), Number(m) - 1, 1)
            return date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
        })
        return {
            labels: displayLabels,
            datasets: [
                {
                    label: 'Cumulative batches',
                    data: createdSeries,
                    borderColor: brandPalette[0],
                    backgroundColor: brandPaletteAlpha(0.15)[0],
                    fill: true,
                    tension: 0.3,
                    pointRadius: 3,
                    pointHoverRadius: 5,
                },
                {
                    label: 'Cumulative submitted',
                    data: submittedSeries,
                    borderColor: brandPalette[3],
                    backgroundColor: brandPaletteAlpha(0.12)[3],
                    fill: true,
                    tension: 0.3,
                    pointRadius: 3,
                    pointHoverRadius: 5,
                },
            ],
        }
    }, [fundingBatches])

    const pipelineChartData = useMemo(() => {
        const stages = [
            { key: 'api', label: 'API Transferred', match: (b: FundingBatch) => (b.api_transferred || 0) > 0 },
            {
                key: 'submitted',
                label: 'Submitted',
                match: (b: FundingBatch) => (b.submitted || 0) > 0 && !(b.api_transferred || 0),
            },
            {
                key: 'ate_returned',
                label: 'ATE Returned',
                match: (b: FundingBatch) =>
                    !!b.config?.ate_returned && !(b.submitted || 0) && !(b.api_transferred || 0),
            },
            {
                key: 'ate_requested',
                label: 'ATE Requested',
                match: (b: FundingBatch) =>
                    !!b.config?.ate_requested &&
                    !b.config?.ate_returned &&
                    !(b.submitted || 0) &&
                    !(b.api_transferred || 0),
            },
            {
                key: 'pending',
                label: 'Pending',
                match: (b: FundingBatch) =>
                    !b.config?.ate_requested && !(b.submitted || 0) && !(b.api_transferred || 0),
            },
        ]
        const counts = stages.map((stage) => fundingBatches.filter(stage.match).length)
        return {
            labels: stages.map((s) => s.label),
            datasets: [
                {
                    data: counts,
                    backgroundColor: brandPalette.slice(0, stages.length),
                    borderColor: '#ffffff',
                    borderWidth: 2,
                },
            ],
        }
    }, [fundingBatches])

    // Filter and sort for table (client-side only)
    const filteredAndSortedBatches = useMemo(() => {
        const searchLower = batchIdSearch.trim().toLowerCase()
        let list = fundingBatches

        if (searchLower) {
            list = list.filter((b) => (b.batch_id ?? '').toLowerCase().includes(searchLower))
        }
        if (statusFilter === 'requested') {
            list = list.filter((b) => b.config?.ate_requested)
        } else if (statusFilter === 'not_requested') {
            list = list.filter((b) => !b.config?.ate_requested)
        }

        const cmp = (a: FundingBatch, b: FundingBatch): number => {
            if (sortBy === 'nera_id') {
                const aNera = a.config?.nera_id || ''
                const bNera = b.config?.nera_id || ''
                const aEmpty = !aNera
                const bEmpty = !bNera
                if (aEmpty && bEmpty) return 0
                if (aEmpty) return 1
                if (bEmpty) return -1
                const aNum = parseInt(aNera.replace(/\D/g, ''), 10) || 0
                const bNum = parseInt(bNera.replace(/\D/g, ''), 10) || 0
                return sortDesc ? bNum - aNum : aNum - bNum
            }

            let aVal: number | string | boolean
            let bVal: number | string | boolean
            switch (sortBy) {
                case 'status':
                    aVal = a.config?.ate_requested ? 1 : 0
                    bVal = b.config?.ate_requested ? 1 : 0
                    break
                case 'value':
                    aVal = a.draw_down_value ?? 0
                    bVal = b.draw_down_value ?? 0
                    break
                case 'clients':
                    aVal = a.total_clients ?? 0
                    bVal = b.total_clients ?? 0
                    break
                case 'batch_id':
                    aVal = (a.batch_id ?? '').toLowerCase()
                    bVal = (b.batch_id ?? '').toLowerCase()
                    break
                case 'created':
                default:
                    aVal = new Date(a.created_at || a.allocation_date || 0).getTime()
                    bVal = new Date(b.created_at || b.allocation_date || 0).getTime()
                    break
            }
            if (typeof aVal === 'number' && typeof bVal === 'number') return sortDesc ? bVal - aVal : aVal - bVal
            if (typeof aVal === 'string' && typeof bVal === 'string') return sortDesc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal)
            return 0
        }
        return [...list].sort(cmp)
    }, [fundingBatches, batchIdSearch, statusFilter, sortBy, sortDesc])

    const handleSort = (field: SortField) => {
        if (sortBy === field) {
            setSortDesc((d) => !d)
        } else {
            setSortBy(field)
            setSortDesc(field === 'created' || field === 'value' || field === 'clients' || field === 'nera_id' ? true : false)
        }
    }

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A'
        try {
            return new Date(dateString).toLocaleDateString('en-GB', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            })
        } catch {
            return dateString
        }
    }

    const formatDateOnly = (dateString: string) => {
        if (!dateString) return 'N/A'
        try {
            return new Date(dateString).toLocaleDateString('en-GB', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            })
        } catch {
            return dateString
        }
    }

    return (
        <div className={styles.allocateClaimsContainer}>
            <div className={styles.allocationGroupsContainer}>
                <div className={styles.tableHeader}>
                    <h3>Funding Batches</h3>
                    {fundingBatchesLoading && <span className={styles.loadingText}>Loading...</span>}
                </div>

                {!fundingBatchesLoading && fundingBatches.length > 0 && (
                    <div className={styles.metricsPanel}>
                        <div className={styles.headlineStats}>
                            <div className={`${styles.headlineCard} ${styles.accentSlate}`}>
                                <span className={styles.headlineIcon}>
                                    <FaLayerGroup />
                                </span>
                                <div className={styles.headlineBody}>
                                    <span className={styles.headlineLabel}>Total Batches</span>
                                    <span className={styles.headlineValue}>{formatCount(stats.totalBatches)}</span>
                                    <span className={styles.headlineCaption}>
                                        {derived.avgClientsPerBatch.toFixed(0)} clients per batch on average
                                    </span>
                                </div>
                            </div>
                            <div className={`${styles.headlineCard} ${styles.accentGreen}`}>
                                <span className={styles.headlineIcon}>
                                    <FaUsers />
                                </span>
                                <div className={styles.headlineBody}>
                                    <span className={styles.headlineLabel}>Total Clients</span>
                                    <span className={styles.headlineValue}>{formatCount(stats.totalClients)}</span>
                                    <span className={styles.headlineCaption}>
                                        {formatCompactCurrency(derived.avgValuePerClient)} average book value
                                    </span>
                                </div>
                            </div>
                            <div className={`${styles.headlineCard} ${styles.accentBlue}`}>
                                <span className={styles.headlineIcon}>
                                    <FaFileAlt />
                                </span>
                                <div className={styles.headlineBody}>
                                    <span className={styles.headlineLabel}>Total Claims</span>
                                    <span className={styles.headlineValue}>{formatCount(stats.totalClaims)}</span>
                                    <span className={styles.headlineCaption}>
                                        {derived.avgClaimsPerClient.toFixed(1)} claims per client
                                    </span>
                                </div>
                            </div>
                            <div className={`${styles.headlineCard} ${styles.accentTeal}`}>
                                <span className={styles.headlineIcon}>
                                    <FaPoundSign />
                                </span>
                                <div className={styles.headlineBody}>
                                    <span className={styles.headlineLabel}>Total Book Value</span>
                                    <span className={styles.headlineValue}>
                                        {formatCompactCurrency(stats.totalBookValue)}
                                    </span>
                                    <span className={styles.headlineCaption}>
                                        {formatCompactCurrency(stats.totalDrawDownValue)} draw down
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className={styles.metricsColumns}>
                            <div className={styles.metricsBlock}>
                                <span className={styles.metricsBlockTitle}>Audit removals</span>
                                <div className={styles.miniStatRow}>
                                    <span className={`${styles.miniStatDot} ${styles.dotRed}`} />
                                    <span className={styles.miniStatLabel}>Clients removed</span>
                                    <span className={styles.miniStatValue}>
                                        {formatCount(stats.auditRemovedClients)}
                                    </span>
                                    <span className={styles.miniStatPct}>
                                        {derived.removedClientPct.toFixed(1)}%
                                    </span>
                                </div>
                                <div className={styles.miniStatRow}>
                                    <span className={`${styles.miniStatDot} ${styles.dotRedSoft}`} />
                                    <span className={styles.miniStatLabel}>Claims removed</span>
                                    <span className={styles.miniStatValue}>
                                        {formatCount(stats.auditRemovedClaims)}
                                    </span>
                                    <span className={styles.miniStatPct}>
                                        {derived.removedClaimPct.toFixed(1)}%
                                    </span>
                                </div>
                                <p className={styles.metricsBlockNote}>
                                    Share of everything ever allocated to a batch.
                                </p>
                            </div>

                            <div className={styles.metricsBlock}>
                                <span className={styles.metricsBlockTitle}>Batch pipeline</span>
                                {[
                                    {
                                        label: 'ATE requested',
                                        value: stats.totalAteRequested,
                                        pct: derived.ateRequestedPct,
                                        className: styles.barAmber,
                                    },
                                    {
                                        label: 'ATE returned',
                                        value: stats.totalAteReturned,
                                        pct: derived.ateReturnedPct,
                                        className: styles.barBlue,
                                    },
                                    {
                                        label: 'Submitted',
                                        value: stats.totalSubmitted,
                                        pct: derived.submittedPct,
                                        className: styles.barGreen,
                                    },
                                    {
                                        label: 'API transferred',
                                        value: stats.totalApiTransferred,
                                        pct: derived.apiTransferredPct,
                                        className: styles.barPurple,
                                    },
                                ].map((row) => (
                                    <div key={row.label} className={styles.progressRow}>
                                        <div className={styles.progressMetaRow}>
                                            <span className={styles.progressLabel}>{row.label}</span>
                                            <span className={styles.progressValue}>
                                                {formatCount(row.value)}
                                                <span className={styles.progressPct}>
                                                    {row.pct.toFixed(0)}%
                                                </span>
                                            </span>
                                        </div>
                                        <div className={styles.progressTrack}>
                                            <div
                                                className={`${styles.progressFill} ${row.className}`}
                                                style={{ width: `${Math.min(100, Math.max(0, row.pct))}%` }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Funded claims by approved-lender category (funder_defendants.category) */}
                {!fundingBatchesLoading && fundingBatches.length > 0 && (
                    <div className={styles.chartsSection}>
                        <div className={`${styles.chartCard} ${styles.chartCardWide}`}>
                            <div className={styles.chartHeader}>
                                <FaChartBar />
                                <div>
                                    <h4>Clients &amp; claims by introducer</h4>
                                    <p>Active clients and claims across all funding batches</p>
                                </div>
                            </div>
                            <div className={styles.chartCanvas}>
                                {introducerChartData.labels.length > 0 ? (
                                    <Bar
                                        data={introducerChartData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { position: 'top', labels: { boxWidth: 12 } },
                                                tooltip: {
                                                    callbacks: {
                                                        label: (ctx) =>
                                                            `${ctx.dataset.label}: ${Number(
                                                                ctx.parsed.y || 0
                                                            ).toLocaleString('en-GB')}`,
                                                    },
                                                },
                                            },
                                            scales: {
                                                x: {
                                                    ticks: { maxRotation: 45, minRotation: 0 },
                                                    grid: { display: false },
                                                },
                                                y: {
                                                    beginAtZero: true,
                                                    ticks: {
                                                        callback: (value) =>
                                                            Number(value).toLocaleString('en-GB'),
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                ) : (
                                    <div className={styles.chartPlaceholder}>No introducer data</div>
                                )}
                            </div>
                        </div>

                        <div className={styles.chartCard}>
                            <div className={styles.chartHeader}>
                                <FaChartLine />
                                <div>
                                    <h4>Batches over time</h4>
                                    <p>Cumulative created vs submitted by allocation month</p>
                                </div>
                            </div>
                            <div className={styles.chartCanvas}>
                                {batchesOverTimeData.labels.length > 0 ? (
                                    <Line
                                        data={batchesOverTimeData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            interaction: { mode: 'index', intersect: false },
                                            plugins: {
                                                legend: { position: 'top', labels: { boxWidth: 12 } },
                                            },
                                            scales: {
                                                x: { grid: { display: false } },
                                                y: {
                                                    beginAtZero: true,
                                                    ticks: {
                                                        precision: 0,
                                                        callback: (value) =>
                                                            Number(value).toLocaleString('en-GB'),
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                ) : (
                                    <div className={styles.chartPlaceholder}>No time-series data</div>
                                )}
                            </div>
                        </div>

                        <div className={styles.chartCard}>
                            <div className={styles.chartHeader}>
                                <FaChartPie />
                                <div>
                                    <h4>Pipeline stages</h4>
                                    <p>Where each batch sits in the funding flow</p>
                                </div>
                            </div>
                            <div className={styles.chartCanvas}>
                                <Doughnut
                                    data={pipelineChartData}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: {
                                            legend: { position: 'right', labels: { boxWidth: 12 } },
                                            tooltip: {
                                                callbacks: {
                                                    label: (ctx) =>
                                                        `${ctx.label}: ${ctx.parsed} batch(es)`,
                                                },
                                            },
                                        },
                                        cutout: '58%',
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {fundingBatchesLoading ? (
                    <div className={styles.loadingState}>
                        <p>Loading funding batches...</p>
                    </div>
                ) : fundingBatches.length > 0 ? (
                    <>
                        <div className={styles.tableToolbar}>
                            <div className={styles.searchGroup}>
                                <label htmlFor="batch-id-search" className={styles.toolbarLabel}>
                                    Search by Batch ID
                                </label>
                                <input
                                    id="batch-id-search"
                                    type="text"
                                    placeholder="Filter by batch ID..."
                                    value={batchIdSearch}
                                    onChange={(e) => setBatchIdSearch(e.target.value)}
                                    className={styles.searchInput}
                                />
                            </div>
                            <div className={styles.filterGroup}>
                                <label htmlFor="status-filter" className={styles.toolbarLabel}>
                                    Status
                                </label>
                                <select
                                    id="status-filter"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                                    className={styles.statusSelect}
                                >
                                    <option value="all">All</option>
                                    <option value="requested">ATE Requested</option>
                                    <option value="not_requested">ATE Not Requested</option>
                                </select>
                            </div>
                            {filteredAndSortedBatches.length < fundingBatches.length && (
                                <span className={styles.filterSummary}>
                                    Showing {filteredAndSortedBatches.length} of {fundingBatches.length} batches
                                </span>
                            )}
                        </div>
                        <div className={styles.tableWrapper}>
                            <table className={styles.allocationTable}>
                                <thead>
                                    <tr>
                                        <th>
                                            <button
                                                type="button"
                                                className={styles.sortableTh}
                                                onClick={() => handleSort('batch_id')}
                                            >
                                                Batch ID {sortBy === 'batch_id' && (sortDesc ? '↓' : '↑')}
                                            </button>
                                        </th>
                                        <th>
                                            <button
                                                type="button"
                                                className={styles.sortableTh}
                                                onClick={() => handleSort('nera_id')}
                                            >
                                                Nera ID {sortBy === 'nera_id' && (sortDesc ? '↓' : '↑')}
                                            </button>
                                        </th>
                                        <th>
                                            <button
                                                type="button"
                                                className={styles.sortableTh}
                                                onClick={() => handleSort('created')}
                                            >
                                                Created At {sortBy === 'created' && (sortDesc ? '↓' : '↑')}
                                            </button>
                                        </th>
                                        <th>Introducer</th>
                                        <th>
                                            <button
                                                type="button"
                                                className={styles.sortableTh}
                                                onClick={() => handleSort('clients')}
                                            >
                                                Total Clients {sortBy === 'clients' && (sortDesc ? '↓' : '↑')}
                                            </button>
                                        </th>
                                        <th>Book Value</th>
                                        <th>
                                            <button
                                                type="button"
                                                className={styles.sortableTh}
                                                onClick={() => handleSort('value')}
                                            >
                                                Draw Down Value {sortBy === 'value' && (sortDesc ? '↓' : '↑')}
                                            </button>
                                        </th>
                                        <th>
                                            <button
                                                type="button"
                                                className={styles.sortableTh}
                                                onClick={() => handleSort('status')}
                                            >
                                                ATE Status {sortBy === 'status' && (sortDesc ? '↓' : '↑')}
                                            </button>
                                        </th>
                                        <th>Submitted / API</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAndSortedBatches.map((batch) => {
                                    const ateRequested = batch.config?.ate_requested
                                    const ateRequestedDate = batch.config?.ate_requested_date
                                    return (
                                        <tr
                                            key={batch.batch_id}
                                            className={styles.clickableRow}
                                            onClick={() => handleViewDetails(batch.batch_id)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault()
                                                    handleViewDetails(batch.batch_id)
                                                }
                                            }}
                                        >
                                            <td>{batch.batch_id || 'N/A'}</td>
                                            <td>{batch.config?.nera_id || '-'}</td>
                                            <td>{formatDateOnly(batch.allocation_date || batch.created_at || '')}</td>
                                            <td>{batch.introducer || 'N/A'}</td>
                                            <td>{batch.total_clients || 0}</td>
                                            <td>
                                                {batch.book_value
                                                    ? `£${batch.book_value.toLocaleString('en-GB', {
                                                          minimumFractionDigits: 2,
                                                          maximumFractionDigits: 2,
                                                      })}`
                                                    : '£0.00'}
                                            </td>
                                            <td>
                                                {batch.draw_down_value
                                                    ? `£${batch.draw_down_value.toLocaleString('en-GB', {
                                                          minimumFractionDigits: 2,
                                                          maximumFractionDigits: 2,
                                                      })}`
                                                    : '£0.00'}
                                            </td>
                                            <td>
                                                <div className={styles.ateStatusCell}>
                                                    <span
                                                        className={`${styles.ateStatusBadge} ${
                                                            ateRequested ? styles.ateRequested : styles.ateNotRequested
                                                        }`}
                                                    >
                                                        {ateRequested ? 'Requested' : 'Not Requested'}
                                                    </span>
                                                    {ateRequestedDate && (
                                                        <span className={styles.ateDate}>
                                                            {formatDate(ateRequestedDate)}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className={styles.submittedApiCell}>
                                                <span
                                                    className={`${styles.statusPill} ${
                                                        (batch.submitted ?? 0) > 0 ? styles.statusPillSuccess : styles.statusPillPending
                                                    }`}
                                                >
                                                    {(batch.submitted ?? 0) > 0 ? 'Submitted' : 'Not started'}
                                                </span>
                                                <span
                                                    className={`${styles.statusPill} ${
                                                        (batch.api_transferred ?? 0) > 0 ? styles.statusPillSuccess : styles.statusPillPending
                                                    }`}
                                                >
                                                    {(batch.api_transferred ?? 0) > 0 ? 'API Transferred' : 'Not started'}
                                                </span>
                                            </td>
                                        </tr>
                                    )
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {filteredAndSortedBatches.length === 0 && (
                            <div className={styles.noResults}>
                                <p>No batches match your search or filter.</p>
                            </div>
                        )}
                    </>
                ) : (
                    <div className={styles.noResults}>
                        <p>No funding batches found.</p>
                    </div>
                )}
            </div>

            {!isScoreUser && (
                <div className={styles.batchControlsSection}>
                    <div
                        className={styles.batchControlsHeader}
                        onClick={() => setBatchControlsOpen(!batchControlsOpen)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                setBatchControlsOpen(!batchControlsOpen)
                            }
                        }}
                    >
                        <div className={styles.batchControlsHeaderText}>
                            <h3>
                                <FaCogs className={styles.batchControlsIcon} />
                                Batch Controls
                            </h3>
                            <p>
                                Admin tools for building batch reports, bringing new funding batches in from a
                                spreadsheet, or permanently clearing fixed clients out of the funding monitor.
                            </p>
                        </div>
                        <FaChevronDown
                            className={`${styles.chevron} ${batchControlsOpen ? styles.chevronOpen : ''}`}
                        />
                    </div>

                    {batchControlsOpen && (
                        <div className={styles.batchControlsBody}>
                            <div className={styles.batchControlCards}>
                                <BatchReportBuilder fundingBatches={fundingBatches} />

                                <div className={styles.batchControlCard}>
                                    <div className={styles.batchControlCardHeader}>
                                        <span className={`${styles.batchControlBadge} ${styles.badgeImport}`}>
                                            <FaFileExcel />
                                        </span>
                                        <div>
                                            <h4>Import batch from spreadsheet</h4>
                                            <p>
                                                Upload an Excel (.xlsx) allocation sheet to create a new funding batch
                                                with clients, claims, and batch config in one step.
                                            </p>
                                        </div>
                                    </div>

                                    <div className={styles.importUploadArea}>
                                        <label className={styles.importFileLabel}>
                                            <FaUpload style={{ marginRight: 6 }} />
                                            {importFile ? importFile.name : 'Select .xlsx file'}
                                            <input
                                                type="file"
                                                accept=".xlsx"
                                                onChange={handleImportFileChange}
                                                ref={importFileRef}
                                                style={{ display: 'none' }}
                                            />
                                        </label>
                                    </div>

                                    {importPreviewLoading && (
                                        <p className={styles.importStatusText}>Analysing spreadsheet...</p>
                                    )}

                                    {importError && <p className={styles.importErrorText}>{importError}</p>}

                                    {importPreview && (
                                        <>
                                            <div className={styles.importPreviewGrid}>
                                                <div className={styles.importPreviewCard}>
                                                    <span className={styles.importPreviewValue}>
                                                        {importPreview.uniqueClients.toLocaleString()}
                                                    </span>
                                                    <span className={styles.importPreviewLabel}>Clients</span>
                                                </div>
                                                <div className={styles.importPreviewCard}>
                                                    <span className={styles.importPreviewValue}>
                                                        {importPreview.totalClaims.toLocaleString()}
                                                    </span>
                                                    <span className={styles.importPreviewLabel}>Claims</span>
                                                </div>
                                                <div className={styles.importPreviewCard}>
                                                    <span className={styles.importPreviewValue}>
                                                        {importPreview.introducers.length}
                                                    </span>
                                                    <span className={styles.importPreviewLabel}>Introducers</span>
                                                </div>
                                                <div className={styles.importPreviewCard}>
                                                    <span className={styles.importPreviewValue}>
                                                        {importPreview.facilities.join(', ') || 'N/A'}
                                                    </span>
                                                    <span className={styles.importPreviewLabel}>Facility</span>
                                                </div>
                                            </div>

                                            <p className={styles.importIntroducersList}>
                                                <strong>Introducers:</strong> {importPreview.introducers.join(', ')}
                                            </p>

                                            <div className={styles.importConfigGrid}>
                                                <div className={styles.importConfigField}>
                                                    <label>
                                                        Batch Group <span className={styles.required}>*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={importBatchGroup}
                                                        onChange={(e) => setImportBatchGroup(e.target.value)}
                                                        placeholder="Batch group name"
                                                    />
                                                </div>
                                                <div className={styles.importConfigField}>
                                                    <label>Commission Rate (%)</label>
                                                    <input
                                                        type="number"
                                                        value={importCommissionRate}
                                                        onChange={(e) => setImportCommissionRate(e.target.value)}
                                                        step="0.1"
                                                    />
                                                </div>
                                                <div className={styles.importConfigField}>
                                                    <label>Stat Interest (%)</label>
                                                    <input
                                                        type="number"
                                                        value={importStatInterest}
                                                        onChange={(e) => setImportStatInterest(e.target.value)}
                                                        step="0.1"
                                                    />
                                                </div>
                                                <div className={styles.importConfigField}>
                                                    <label>Min Client Value</label>
                                                    <input
                                                        type="number"
                                                        value={importMinClientValue}
                                                        onChange={(e) => setImportMinClientValue(e.target.value)}
                                                    />
                                                </div>
                                                <div className={styles.importConfigField}>
                                                    <label>Calculation Date</label>
                                                    <input
                                                        type="date"
                                                        value={importCalcDate}
                                                        onChange={(e) => setImportCalcDate(e.target.value)}
                                                    />
                                                </div>
                                            </div>

                                            <div className={styles.importActions}>
                                                <button
                                                    className={styles.importButton}
                                                    onClick={handleImportSubmit}
                                                    disabled={importLoading || !importBatchGroup}
                                                >
                                                    {importLoading ? 'Importing...' : 'Import Batch'}
                                                </button>
                                            </div>
                                        </>
                                    )}

                                    {importResult && (
                                        <div className={styles.importResultCard}>
                                            <h4>Import Complete</h4>
                                            <p>
                                                Batch <strong>{importResult.batchGroup}</strong> imported successfully.
                                            </p>
                                            <div className={styles.importResultGrid}>
                                                <div>
                                                    <span className={styles.importResultValue}>
                                                        {importResult.summary.uniqueClients}
                                                    </span>
                                                    <span className={styles.importResultLabel}>Clients</span>
                                                </div>
                                                <div>
                                                    <span className={styles.importResultValue}>
                                                        {importResult.summary.totalClaims}
                                                    </span>
                                                    <span className={styles.importResultLabel}>Claims</span>
                                                </div>
                                                <div>
                                                    <span className={styles.importResultValue}>
                                                        {importResult.insertResults.totalInserted}
                                                    </span>
                                                    <span className={styles.importResultLabel}>Inserted</span>
                                                </div>
                                                <div>
                                                    <span className={styles.importResultValue}>
                                                        {importResult.insertResults.totalFailed}
                                                    </span>
                                                    <span className={styles.importResultLabel}>Failed</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className={`${styles.batchControlCard} ${styles.batchControlCardDanger}`}>
                                    <div className={styles.batchControlCardHeader}>
                                        <span className={`${styles.batchControlBadge} ${styles.badgeRemove}`}>
                                            <FaTrashAlt />
                                        </span>
                                        <div>
                                            <h4>Remove fixed clients</h4>
                                            <p>
                                                Upload a CSV of client emails to permanently delete matching rows from
                                                funder_clients, funder_claims, and funder_references so they can be
                                                re-batched. This cannot be undone.
                                            </p>
                                        </div>
                                    </div>

                                    <div className={styles.importUploadArea}>
                                        <label className={styles.importFileLabel}>
                                            <FaFileCsv style={{ marginRight: 6 }} />
                                            {removeFile ? removeFile.name : 'Select .csv file'}
                                            <input
                                                type="file"
                                                accept=".csv,text/csv"
                                                onChange={handleRemoveFileChange}
                                                ref={removeFileRef}
                                                style={{ display: 'none' }}
                                            />
                                        </label>
                                    </div>

                                    {removePreviewLoading && (
                                        <p className={styles.importStatusText}>Analysing CSV…</p>
                                    )}

                                    {removeError && <p className={styles.importErrorText}>{removeError}</p>}

                                    {removePreview && (
                                        <>
                                            <div className={styles.importPreviewGrid}>
                                                <div className={styles.importPreviewCard}>
                                                    <span className={styles.importPreviewValue}>
                                                        {removePreview.requestedEmails.toLocaleString()}
                                                    </span>
                                                    <span className={styles.importPreviewLabel}>Emails in file</span>
                                                </div>
                                                <div className={styles.importPreviewCard}>
                                                    <span className={styles.importPreviewValue}>
                                                        {removePreview.matchedClients.toLocaleString()}
                                                    </span>
                                                    <span className={styles.importPreviewLabel}>Clients to remove</span>
                                                </div>
                                                <div className={styles.importPreviewCard}>
                                                    <span className={styles.importPreviewValue}>
                                                        {removePreview.notFoundCount.toLocaleString()}
                                                    </span>
                                                    <span className={styles.importPreviewLabel}>Not in database</span>
                                                </div>
                                            </div>

                                            {removePreview.notFoundCount > 0 &&
                                            removePreview.notFoundSample?.length ? (
                                                <p className={styles.importIntroducersList}>
                                                    <strong>Sample unmatched emails:</strong>{' '}
                                                    {removePreview.notFoundSample.slice(0, 12).join(', ')}
                                                    {removePreview.notFoundSample.length > 12 ? '…' : ''}
                                                </p>
                                            ) : null}

                                            <label className={styles.removeConfirmRow}>
                                                <input
                                                    type="checkbox"
                                                    checked={removeConfirmed}
                                                    onChange={(e) => setRemoveConfirmed(e.target.checked)}
                                                />
                                                <span>
                                                    I confirm these clients should be permanently removed from
                                                    funding-monitor.
                                                </span>
                                            </label>

                                            <div className={styles.importActions}>
                                                <button
                                                    type="button"
                                                    className={styles.removeDangerButton}
                                                    onClick={handleRemoveSubmit}
                                                    disabled={
                                                        removeLoading ||
                                                        !removeConfirmed ||
                                                        removePreview.matchedClients === 0
                                                    }
                                                >
                                                    {removeLoading ? 'Removing…' : 'Remove from funding monitor'}
                                                </button>
                                            </div>
                                        </>
                                    )}

                                    {removeResult && (
                                        <div className={styles.removeResultCard}>
                                            <h4>Removal complete</h4>
                                            <p>
                                                Processed {removeResult.requestedEmails} email(s); matched{' '}
                                                {removeResult.matchedClients} client(s).
                                                {removeResult.notFoundCount > 0
                                                    ? ` ${removeResult.notFoundCount} email(s) had no matching client.`
                                                    : ''}
                                            </p>
                                            <div className={styles.importResultGrid}>
                                                <div>
                                                    <span className={styles.importResultValue}>
                                                        {removeResult.deletionResults.funder_clients}
                                                    </span>
                                                    <span className={styles.importResultLabel}>funder_clients</span>
                                                </div>
                                                <div>
                                                    <span className={styles.importResultValue}>
                                                        {removeResult.deletionResults.funder_claims}
                                                    </span>
                                                    <span className={styles.importResultLabel}>funder_claims</span>
                                                </div>
                                                <div>
                                                    <span className={styles.importResultValue}>
                                                        {removeResult.deletionResults.funder_references}
                                                    </span>
                                                    <span className={styles.importResultLabel}>
                                                        funder_references
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
